import React, { useCallback, useLayoutEffect, useMemo } from 'react'
import './split-step-node.scss'
import { Handle, NodeProps, NodeToolbar, Position, useUpdateNodeInternals } from '@xyflow/react'
import { getEdgeId } from '../../helpers'
import { JourneyNodeIcon } from '../custom-node/journey-node-icon'
import { JourneyNodeLabel } from '../custom-node/journey-node-label'
import { getClassNames } from '../../../../_utils/classnames'
import { useJourneyContext } from '../../context'
import { JourneyNode, isVariantSplitStep } from '../../types/journey-nodes'
import { InvalidJourneyComponent } from '../../edge-types/custom-edge/invalid-journey-component'
import { JourneyNodeInteractionActions } from '../custom-node/journey-node-interaction-actions'
import { JourneyAction } from '../../types/journey-context'
import { JourneyBuilderMode } from '../../enums/journey-builder-mode.enum'
import { useSplitTestResultsContext } from '../../split-test-results/split-test-results-context'
import { Button, Tooltip } from 'antd'
import { BarChartOutlined } from '@ant-design/icons'
import { StatusType } from '../../../../enums/status-type'

export const SplitStepNode = ({ id, data, targetPosition, sourcePosition }: NodeProps<JourneyNode>) => {
    const [state, dispatch] = useJourneyContext()
    const { openResultsPanel } = useSplitTestResultsContext()
    const updateNodeInternals = useUpdateNodeInternals()
    const step = data.step
    if (!isVariantSplitStep(step)) return null
    const branches = step.configuration.branches ?? []

    const isDraft = state.journey?.status === StatusType.DRAFT.name
    const isReadonly = state.mode === JourneyBuilderMode.READONLY

    // Refresh React Flow's handle registry when handle IDs/positions change.
    // This covers cases where branch count returns to the same value (e.g. add C, remove B).
    const handleSignature = useMemo(() => {
        const elkPortMap = new Map((data.uiMeta.elkPortPositions ?? []).map((p) => [p.portId, p.leftPercent]))
        return branches
            .map((branch, idx) => {
                const portId = `branch-${branch.key}`
                const fallbackLeftPercent = ((idx + 1) / (branches.length + 1)) * 100
                const leftPercent = elkPortMap.get(portId) ?? fallbackLeftPercent
                return `${portId}:${leftPercent.toFixed(3)}`
            })
            .join('|')
    }, [branches, data.uiMeta.elkPortPositions])

    useLayoutEffect(() => {
        updateNodeInternals(id)
    }, [id, branches.length, handleSignature, updateNodeInternals])

    const handleNodeBodyClick = useCallback(() => {
        dispatch({ action: JourneyAction.CLOSE_ALL_POPOVERS })
    }, [dispatch])

    return (
        <React.Fragment>
            <Handle type="target" position={targetPosition ?? Position.Top} id={getEdgeId(id)} />
            <NodeToolbar isVisible={!!data.uiMeta.invalid} position={Position.Left} className="journey-invalid-label">
                <InvalidJourneyComponent content={data.uiMeta.invalid?.reason} />
            </NodeToolbar>
            <div
                className={getClassNames(
                    null,
                    `custom-node-wrapper type-variant_split`,
                    {
                        invalid: data.uiMeta.invalid,
                    },
                )}
                onClick={handleNodeBodyClick}
            >
                <div className="custom-node-border" />
                <div className={getClassNames(null, 'custom-node', 'no-drag')}>
                    <JourneyNodeIcon id={id} node={step} />
                    <JourneyNodeLabel node={step} />
                    {/* NS-2599: results entry point is now an insights ICON button
                        in the SAME node-action row as edit/delete (revealed on
                        hover like the others). Hidden on draft journeys (no data
                        to show yet) and in readonly mode. */}
                    <JourneyNodeInteractionActions
                        nodeId={id}
                        extraActions={
                            !isDraft && !isReadonly ? (
                                <Tooltip key="split-results" title="View split test results">
                                    <Button
                                        className="journey-node-results-button"
                                        icon={<BarChartOutlined />}
                                        aria-label="View split test results"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            openResultsPanel(id)
                                        }}
                                    />
                                </Tooltip>
                            ) : null
                        }
                    />
                </div>
            </div>
            {branches.map((branch, idx) => {
                // Prefer ELK-computed port positions when available so handles
                // align with the actual edge start points ELK calculated.
                const elkPorts = data.uiMeta.elkPortPositions
                const elkPort = elkPorts?.find(p => p.portId === `branch-${branch.key}`)
                const leftPercent = elkPort
                    ? elkPort.leftPercent
                    : ((idx + 1) / (branches.length + 1)) * 100

                return (
                    <Handle
                        key={branch.key}
                        type="source"
                        position={sourcePosition ?? Position.Bottom}
                        id={`branch-${branch.key}`}
                        style={{
                            left: `${leftPercent}%`,
                        }}
                    />
                )
            })}
        </React.Fragment>
    )
}
